import { Deferred } from '@buzzfeed/buzzblocks/js/services/util/standalone';
import { framework, rules, api } from '@buzzfeed/consent';
import cookies from '@buzzfeed/bf-utils/lib/cookies';
import { debugMode } from '../../core/debug-mode/index.js';

/**
 * `rules.hasConsented` uses the `getTCData` command,
 * which is required by the spec to call the callback
 * "with whatever is available in the current state of the CMP",
 * so if `rules.hasConsented` is called while the CMP UI is shown,
 * no meaningful consent data is available at that moment.
 * Instead, ads will check consent data only when it has been set.
 * @see https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/a23391d22a12aca49e35db20f73a7542f90033bd/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md#gettcdata
 */

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

var consentIsKnown = new Deferred();

function consentCheck() {
  if (!framework.needsGDPRConsent()) {
    debugMode('info', 'consent', 'consentIsKnown resolved, no need for consent');
    consentIsKnown.resolve();
  } else {
    api.getTCData().then(function (_ref) {
      var tcData = _ref.tcData;

      if (!tcData.gdprApplies) {
        debugMode('info', 'consent', 'consentIsKnown resolved, there is a gdpr cookie but gdpr does not apply');
        consentIsKnown.resolve();
      }
    });
  } // see https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/a23391d22a12aca49e35db20f73a7542f90033bd/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md#addeventlistener


  api.setTCFListener(function (_ref2) {
    var tcData = _ref2.tcData;
    debugMode('info', 'consent', 'tcf event:', tcData.eventStatus);

    if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
      consentIsKnown.resolve();
      debugMode('info', 'consent', 'consentIsKnown resolved, user action complete');
    } // when the user rejects gdpr consent
    // remove AMZN-Token here because we don't load TAM


    rules.hasConsented('ads').then(function (adsConsent) {
      if (tcData.eventStatus === 'useractioncomplete' && tcData.gdprApplies && !adsConsent) {
        cookies.remove('AMZN-Token', '');
      }
    });
  });
}

consentCheck();
var consentAds = {
  /**
   * gets from window.__tcfapi to check if user has consented to ads purpose
   * @return {Promise} a promise that returns a bool
   */
  fetchAdPurposeConsent: function fetchAdPurposeConsent() {
    try {
      // if we don't need consent from them, just let them through
      if (!framework.needsGDPRConsent()) {
        return Promise.resolve(true);
      }

      return _await(consentIsKnown, function () {
        return rules.hasConsented('ads');
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },

  /**
   * checks if user has consented. return true in areas where
   * we dont need to have consent from the user (non-EU)
   * @return {Boolean} true if user has consented
   */
  hasConsented: function hasConsented() {
    return consentAds.fetchAdPurposeConsent();
  },

  /**
   * check if user has consented to google specificially.
   * similar to hasConsented, but fallbacks to typical ad purpose
   * if not using the QC Choice CMP
   */
  hasConsentedGoogle: function hasConsentedGoogle() {
    try {
      if (!framework.needsGDPRConsent()) {
        return Promise.resolve(true);
      }

      return _await(consentIsKnown, function () {
        return rules.hasConsented('google');
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  hasOptedOutCCPA: function hasOptedOutCCPA() {
    if (!framework.needsCCPAConsent()) {
      return Promise.resolve(false);
    }

    return rules.fetchCCPAOptOut();
  },
  needsConsent: framework.needsConsent()
};
var service = {};
Object.defineProperties(service, Object.getOwnPropertyDescriptors(consentAds));

export { service as default };
